import { useState, useEffect, useReducer, useCallback, ChangeEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EditorService } from '_common/services';
import { Tooltip, Switch, Tabs, Input, EmptyState, Button, Icon } from '@ets/design-system';

import { useDispatch, useDebounce, useSelector } from '_common/hooks';
import { openAndUpdateModal, closeModal } from '_common/modals/ModalsSlice';
import useConditionalData from '_common/modals/ExportDocumentModal/useConditionalData';
import { useApplyTemplateMutation } from '_common/services/api/realtime';
import EditorManager from 'Editor/services/EditorManager';
import { FixedSizeGrid as Grid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import { Helper, Spinner } from '_common/components';

import Template from './Template';

import IntlErrorBoundary from '../IntlErrorBoundary/IntlErrorBoundary';
import { CARD_WIDTH, CARD_HEIGHT, COLUMN_GAP, ROW_GAP } from './consts';
import cn from 'classnames';

const SEARCH_INPUT_STYLES = { marginLeft: 'auto', width: '28rem' };

type InitialStateProps = {
  loading: boolean;
  data: DocumentTemplate[] | null;
  error: string | null;
  offset: number;
  size: number;
  hasNextPage?: boolean;
  isNextPageLoading?: boolean;
};
type State = {
  data: DocumentTemplate[] | null;
  loading: boolean;
  error: string | null;
  offset: number;
  size: number;
  hasNextPage?: boolean;
  isNextPageLoading?: boolean;
};

type Action =
  | { type: 'RESET' }
  | { type: 'SET_DATA'; payload: DocumentTemplate[] }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_NEXT_PAGE_LOADING'; payload: boolean };

const initialState: InitialStateProps = {
  data: [],
  loading: true,
  error: null,
  offset: 0,
  size: 40,
  hasNextPage: false,
  isNextPageLoading: false,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'RESET':
      return { ...initialState };
    case 'SET_DATA': {
      const existingIds = new Set(state.data?.map((template) => template.id));
      const newItems = action.payload.filter((template) => !existingIds.has(template.id));
      const newData = [...(state?.data ?? []), ...newItems];
      return {
        ...state,
        data: newData,
        offset: state.offset + newItems.length,
        loading: false,
        isNextPageLoading: false,
        hasNextPage: action.payload.length >= state.size,
        error: null,
      };
    }
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false, isNextPageLoading: false };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_NEXT_PAGE_LOADING':
      return { ...state, isNextPageLoading: action.payload };
    default:
      return state;
  }
};

type TemplateGalleryProps = {
  objectId: ObjectId;
  close: () => void;
  modal?: boolean;
};

const TemplateGallery = ({ objectId, close, modal }: TemplateGalleryProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | undefined>();
  const [{ loading, data, error, offset, size, hasNextPage, isNextPageLoading }, dispatchReducer] =
    useReducer(reducer, initialState);
  const [filterValue, setFilterValue] = useState('');
  const [removeInlineStyles, setRemoveInlineStyles] = useState(false);
  const debouncedFilterValue = useDebounce(filterValue, 500);

  const isSearching = debouncedFilterValue.trim().length > 0;
  const isEditor = useSelector((state) => state.editor.status.visible);
  const { template: activeTemplate } = useConditionalData(objectId);
  const [applyTemplate] = useApplyTemplateMutation();

  const setData = (data: DocumentTemplate[]) =>
    dispatchReducer({ type: 'SET_DATA', payload: data });
  const setError = (error: any) => dispatchReducer({ type: 'SET_ERROR', payload: error });
  const setLoading = (loading: boolean) =>
    dispatchReducer({ type: 'SET_LOADING', payload: loading });
  const setNextPageLoading = (isLoading: boolean) =>
    dispatchReducer({ type: 'SET_NEXT_PAGE_LOADING', payload: isLoading });
  const DEFAULT_TEMPLATE: DocumentTemplate = { id: '_blank', name: 'Default' };

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setFilterValue(e.target.value),
    [setFilterValue],
  );

  useEffect(() => {
    dispatchReducer({ type: 'RESET' });
  }, [debouncedFilterValue]);

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const service = new EditorService();
        const response = isSearching
          ? await service.searchTemplates(debouncedFilterValue)
          : await service.listInstalledTemplates({
              offset: 0,
              size,
            });
        /**
         * searchTemplates does NOT return the DEFAULT_TEMPLATE
         * listInstalledTemplates does include it
         */
        const hasDefaultTemplate = response.data.some(
          (template) =>
            template.id === DEFAULT_TEMPLATE.id || template.name === DEFAULT_TEMPLATE.name,
        );
        const templates = hasDefaultTemplate ? response.data : [DEFAULT_TEMPLATE, ...response.data];
        const search = debouncedFilterValue.trim().toLowerCase();
        const filtered = isSearching
          ? templates.filter((t) => t.name.toLowerCase().includes(search))
          : templates;
        setData(filtered);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, [debouncedFilterValue, isSearching]);

  const loadMoreItems = async () => {
    if (isSearching || !hasNextPage || isNextPageLoading) return;

    setNextPageLoading(true);
    try {
      const response = await new EditorService().listInstalledTemplates({ offset, size });
      setData(response.data);
    } catch (error) {
      setError(error);
    } finally {
      setNextPageLoading(false);
    }
  };

  const apply = useCallback(() => {
    const confirmationModalFlow = (objectId?: ObjectId) => {
      dispatch(closeModal('ExportDocumentModal'));
      dispatch(
        openAndUpdateModal({
          modal: 'ConfirmationModal',
          data: {
            message: 'CHANGE_DOCUMENT_TEMPLATE_MESSAGE',
            title: 'CHANGE_DOCUMENT_TEMPLATE',
            confirmButtonTextId: 'CHANGE_DOCUMENT_TEMPLATE',
            cancelButtonTextId: 'global.cancel',
            confirmButtonType: 'primary',
            actionCode: 'changeDocumentTemplate',
            actionValue: {
              removeInlineStyles,
              templateId: selectedTemplate?.id,
              objectId: objectId,
            },
            width: '60rem',
            cancelButtonShow: true,
          },
        }),
      );
    };

    if (isEditor) {
      EditorManager.getInstance()
        .getApprovedNodes()
        .then((response: string | string[]) => {
          if (response.length > 0) {
            confirmationModalFlow();
          } else {
            if (selectedTemplate?.id) {
              EditorManager.getInstance().updateTemplateStyles(
                selectedTemplate.id,
                removeInlineStyles,
              );
            }
          }
        });
    } else if (selectedTemplate && objectId) {
      applyTemplate({
        template: selectedTemplate.id,
        forceReopen: false,
        removeInlineStyles,
        objectId,
      });
    }

    close();
  }, [selectedTemplate, close, removeInlineStyles]);

  return (
    <div
      className={cn(
        'bg-neutral-2 fixed top-0 left-0 z-10001 flex h-screen w-screen flex-col px-16 py-24',
        modal && 'pointer-events-auto',
      )}
    >
      <div className="weight-600 mb-16 flex justify-between text-4xl">
        <FormattedMessage id="templates.gallery.title" />
        <div style={{ cursor: 'pointer' }} onClick={close} data-testid="template-galley-close-icon">
          <Icon icon="xmark" color="neutral" size="3" testId="template-galley-close" />
        </div>
      </div>
      <Tabs
        value="TemplateGallery"
        onValueChange={() => {}}
        className="flex min-h-0 flex-1 flex-col"
      >
        <Tabs.List>
          <Tabs.Trigger value="TemplateGallery">
            {intl.formatMessage({ id: 'TENANT_TEMPLATES' })}
          </Tabs.Trigger>
          <div style={SEARCH_INPUT_STYLES}>
            <Input
              prefix="search"
              placeholder={intl.formatMessage({ id: 'global.search' })}
              value={filterValue}
              onChange={onInputChange}
              size="m"
              testId="template-gallery-search"
            />
          </div>
        </Tabs.List>
        <Tabs.Content value="TemplateGallery" className="flex h-full min-h-0 flex-col">
          <IntlErrorBoundary key="TemplateGallery" size="xl" style={{ margin: '5rem 0' }}>
            {error && (
              <Helper type="warning" testId="template-gallery-error-helper">
                <FormattedMessage id="DOCUMENT_TEMPLATE_GALLERY_LIST_ERROR" />
              </Helper>
            )}
            {loading && (
              <div className="flex justify-center">
                <Spinner size="medium" testId="template-gallery-loading" />
              </div>
            )}
            {!loading && data && data.length > 0 && (
              <div className="flex-1">
                <AutoSizer>
                  {({ height, width }) => {
                    const totalColumnWidth = CARD_WIDTH + COLUMN_GAP;
                    const columnCount = Math.max(1, Math.floor(width / totalColumnWidth));
                    const rowCount = Math.ceil(data.length / columnCount);

                    return (
                      <Grid
                        height={height}
                        width={width}
                        columnCount={columnCount}
                        columnWidth={CARD_WIDTH + COLUMN_GAP}
                        rowCount={rowCount}
                        rowHeight={CARD_HEIGHT + ROW_GAP}
                        onItemsRendered={({ visibleRowStopIndex }) => {
                          const totalRows = Math.ceil(data.length / columnCount);
                          if (hasNextPage && visibleRowStopIndex >= totalRows - 1) {
                            loadMoreItems();
                          }
                        }}
                      >
                        {({ columnIndex, rowIndex, style }) => {
                          const index = rowIndex * columnCount + columnIndex;
                          if (index >= data.length) return null;

                          const template = data[index];
                          return (
                            <div
                              style={{
                                ...style,
                                left: (style.left as number) + 2,
                                top: (style.top as number) + 16,
                              }}
                            >
                              <Template
                                key={template.id}
                                template={template}
                                selectTemplate={setSelectedTemplate}
                                active={
                                  selectedTemplate
                                    ? selectedTemplate.id === template.id
                                    : activeTemplate === template.id
                                }
                              />
                            </div>
                          );
                        }}
                      </Grid>
                    );
                  }}
                </AutoSizer>
              </div>
            )}
            {!loading && data && data.length === 0 && (
              <EmptyState
                size="l"
                icon="magnifying-glass"
                title={
                  data?.length === 0
                    ? intl.formatMessage({ id: 'NO_DOCUMENT_TEMPLATES_AVAILABLE' })
                    : intl.formatMessage({ id: 'SEARCH_EMPTY_TITLE' })
                }
                testId="template-gallery-empty-list"
                description={
                  data?.length === 0 ? '' : intl.formatMessage({ id: 'SEARCH_EMPTY_DESCRIPTION' })
                }
                className="m-auto flex justify-center"
              />
            )}
          </IntlErrorBoundary>
        </Tabs.Content>
      </Tabs>
      <div className="mx-4 mt-16 mb-0 flex justify-end">
        <div className="mr-20 flex items-center">
          <Switch
            onChange={() => setRemoveInlineStyles(!removeInlineStyles)}
            value={removeInlineStyles}
            disabled={!selectedTemplate}
            size="xs"
            className="mr-8"
            testId="template-gallery-delete-existing-document-inline-styles"
            label={intl.formatMessage({ id: 'DELETE_EXISTING_DOCUMENT_INLINE_STYLES' })}
            labelPlacement="right"
          />
          <Tooltip
            content={intl.formatMessage({
              id: 'DELETE_EXISTING_DOCUMENT_INLINE_STYLES_TOOLTIP',
            })}
            testId="delete-existing-inline-styles"
          >
            <span>
              <Icon
                icon="circle-info"
                color="primary"
                size="2"
                testId="delete-existing-inline-styles"
              />
            </span>
          </Tooltip>
        </div>
        <Button
          size="m"
          variant="standard"
          className="mr-4"
          onClick={close}
          testId="template-gallery-close"
        >
          <FormattedMessage id="global.close" />
        </Button>
        <Button
          size="m"
          variant="primary"
          disabled={!selectedTemplate}
          onClick={apply}
          testId="template-gallery-apply-template"
        >
          <FormattedMessage id="APPLY_TEMPLATE" />
        </Button>
      </div>
    </div>
  );
};

export default TemplateGallery;
